// Softphone page content. Structure follows comparable UK softphone pages; copy and visuals are VOIP2's own.
const M = '/media/softphone';

export const SOFTPHONE = {
  hero: {
    title: 'Your business phone,',
    highlight: 'in one app',
    sub: 'Calls, video meetings and team chat in one simple app. Use your VOIP2 number on your computer, your mobile or in the browser, wherever you work.',
    image: `${M}/softphone-hero.webp`,
    bullets: ['Windows & macOS', 'iOS & Android', 'Works in the browser', 'Included with Cloud PBX'],
  },

  chips: ['HD voice', 'Video meetings', 'Team chat', 'Presence', 'Call recording', 'Call forwarding', 'Call queues', 'Auto-attendant', 'Voicemail to email', 'Hold music', 'Custom greetings', 'Contact sync', 'Call reports'],

  intro: {
    title: 'Everything your desk phone did, and more',
    paragraphs: [
      'The VOIP2 softphone turns any computer or mobile into a full business phone. Make and take calls on your company number, transfer them, put them on hold and pick up voicemail, exactly as you would on a desk phone.',
      'Because it is software, it also does what a desk phone cannot: HD video meetings, team chat with presence, a shared company directory and click-to-dial from your contacts.',
    ],
    bullets: ['Your business number on every device', 'No hardware to buy', 'Same login on desktop, mobile and web', 'Set up in minutes'],
  },

  screens: [
    { img: `${M}/screen-calls.webp`, title: 'Calls', body: 'Dial pad, recent calls and full call control: hold, transfer, record, add a call or switch to video.' },
    { img: `${M}/screen-chat.webp`, title: 'Team chat', body: 'One-to-one and group chat with file sharing, and see at a glance who is free to take a call.' },
    { img: '/media/pbx/video-meeting.webp', title: 'Video meetings', body: 'HD video, screen sharing and live captions, started from a chat or a call in one click.' },
    { img: '/media/pbx/softphone.webp', title: 'Mobile app', body: 'Your office extension on iOS and Android, with chat, presence and visual voicemail.' },
  ],

  // Feature groups shown as tabs.
  features: [
    {
      id: 'devices', icon: 'Laptop', title: 'Devices & platforms',
      body: 'One account across every device your team uses.',
      points: ['Desktop app for Windows and macOS', 'Mobile apps for iOS and Android', 'Web phone in Chrome, Edge, Safari and Firefox', 'Works alongside Yealink and Grandstream desk phones'],
    },
    {
      id: 'calls', icon: 'Phone', title: 'Voice & video calls',
      body: 'Crystal-clear calls with every control you expect from a business phone.',
      points: ['HD voice with echo cancellation', 'Hold, transfer, park and three-way calling', 'Switch a call to video in one click', 'Move a live call between desktop and mobile'],
    },
    {
      id: 'meetings', icon: 'Video', title: 'Conferencing',
      body: 'Meetings for the whole team, without another tool.',
      points: ['HD video meetings with screen sharing', 'Conference calls with internal and external callers', 'Live captions and recording transcripts', 'Join from Outlook or Google Calendar'],
    },
    {
      id: 'chat', icon: 'MessageSquare', title: 'Instant messaging',
      body: 'Keep conversations next to your calls.',
      points: ['One-to-one and group chat', 'Share files and images', 'Presence for every colleague', 'Turn a chat into a call or meeting'],
    },
    {
      id: 'contacts', icon: 'Users', title: 'Address book',
      body: 'Everyone you call, in one place.',
      points: ['Shared company directory', 'Sync contacts from Google and Microsoft 365', 'Click-to-dial from any contact', 'Caller name shown before you answer'],
    },
    {
      id: 'integrations', icon: 'Plug', title: 'Integrations',
      body: 'Connect calling to the tools you already use.',
      points: ['250+ CRM and business apps', 'Screen pops with caller details', 'Calls logged to your CRM automatically', 'Single sign-on with Google Workspace or Microsoft 365'],
    },
    {
      id: 'productivity', icon: 'Zap', title: 'Productivity',
      body: 'Small things that save time on every call.',
      points: ['Visual voicemail and voicemail to email', 'Call recording with playback', 'Do not disturb and call forwarding rules', 'Missed-call notifications'],
    },
    {
      id: 'setup', icon: 'Gauge', title: 'Simple setup',
      body: 'From download to first call in minutes.',
      points: ['Sign in with your VOIP2 details', 'Settings provisioned automatically', 'No ports or servers to configure', 'Add or remove users from the portal'],
    },
    {
      id: 'mobility', icon: 'Smartphone', title: 'Mobility',
      body: 'Your office number goes where you go.',
      points: ['Call from your mobile using your business number', 'Keep personal and work calls separate', 'Wi-Fi and mobile data calling', 'Push notifications for incoming calls'],
    },
    {
      id: 'security', icon: 'ShieldCheck', title: 'Security & diagnostics',
      body: 'Encrypted by default, easy to support.',
      points: ['TLS signalling and SRTP encrypted media', 'Call quality indicators during calls', 'Diagnostic logs for our support team', 'Users managed centrally from the VOIP2 portal'],
    },
  ],

  devices: [
    { icon: 'Laptop', title: 'Desktop app', body: 'Windows and macOS, with calls, chat and meetings in one window.' },
    { icon: 'Smartphone', title: 'Mobile app', body: 'iOS and Android, with your office extension in your pocket.' },
    { icon: 'Globe', title: 'Web phone', body: 'Calls straight from the browser, with nothing to install.' },
    { icon: 'Phone', title: 'Desk phones', body: 'Use the app alongside pre-provisioned IP desk phones.' },
  ],

  steps: [
    { verb: 'Download', body: 'Install the app on your computer or phone, or open the web phone.' },
    { verb: 'Sign in', body: 'Use your VOIP2 login. Your extension and settings load automatically.' },
    { verb: 'Start calling', body: 'Make and take calls on your business number straight away.' },
  ],

  faqs: [
    { q: 'What is a softphone?', a: 'A softphone is an app that works like a desk phone. It lets you make and receive calls on your business number from a computer or mobile, using your internet connection.' },
    { q: 'Is the softphone included?', a: 'Yes. The desktop, mobile and web apps are included with every VOIP2 Cloud PBX plan at no extra cost.' },
    { q: 'Which devices does it work on?', a: 'Windows and macOS computers, iOS and Android phones, and any modern browser (Chrome, Edge, Safari or Firefox) through the web phone.' },
    { q: 'Do I need a headset?', a: 'No, but a USB or Bluetooth headset gives the best call quality on a computer. We can supply headsets and IP desk phones if you need them.' },
    { q: 'Can I use it alongside my desk phone?', a: 'Yes. The app and a desk phone can ring together on the same extension, and you can pick up on whichever is closest.' },
    { q: 'Are calls secure?', a: 'Yes. Signalling is encrypted with TLS and call audio with SRTP, so calls are never sent in clear text.' },
  ],

  related: [
    { label: 'Cloud PBX', to: '/products/cloud-pbx' },
    { label: 'AI Receptionist', to: '/products/ai-receptionist' },
    { label: 'DID International Numbers', to: '/phone-numbers/did-international' },
  ],
};
